import React, { useState, useEffect, useCallback } from 'react';
import PersonnelAddPaymentModal from '../PersonnelAddPaymentModal';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../../services/api';
import { createApiUrlWithPrefix, getUserImageUrl } from '../../config/api';
import { useUserCounts } from '../../contexts/UserCountsContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  Search,
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  CreditCard
} from 'lucide-react';
import UserCreationModal from '../UserCreationModal';
import UserEditModal from '../UserEditModal';
import UserDetailsModal from '../UserDetailsModal';
import UserPermissionsDialog from '../UserPermissionsDialog';

function Personnel() {
  const { t, i18n } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const { user: currentUser } = useAuth();
  const isRTL = i18n.language === 'ar';

  // Check if current user is SuperAdmin
  const isSuperAdmin = currentUser?.role === 4 || currentUser?.role === 'SuperAdmin';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);

  // Fetch personnel users (Admin + SuperAdmin)
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching users - Page:', currentPage, 'Search:', searchTerm);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '3', // Admin role (3) - API expects number as string
        _t: Date.now(), // Cache busting parameter
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Filter out SuperAdmin users from the personnel list
      const filteredUsers = (response.data.users || response.data).filter(user => user.role !== 4);
      console.log('📊 Fetched users:', filteredUsers.length, 'Total pages:', response.data.totalPages);

      setUsers(filteredUsers);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
    } catch (error) {
      console.error('Failed to fetch personnel:', error);
      toast.error('Failed to load personnel');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Listen for the custom event from sidebar
  useEffect(() => {
    const handleOpenAddUserModal = (event) => {
      if (event.detail.role === 'Admin') {
        setShowModal(true);
      }
    };

    window.addEventListener('openAddUserModal', handleOpenAddUserModal);
    return () => {
      window.removeEventListener('openAddUserModal', handleOpenAddUserModal);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Event handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');

    // Refresh sidebar counts
    refreshUserCounts();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  const handleManagePermissions = (user) => {
    setPermissionsUser(user);
    setShowPermissionsDialog(true);
  };

  // Dropdown functions
  const handleDropdownToggle = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const handleAction = (action, user) => {
    setDropdownOpen(null);
    switch (action) {
      case 'view':
        handleViewUser(user);
        break;
      case 'edit':
        handleEditUser(user);
        break;
      case 'permissions':
        handleManagePermissions(user);
        break;
      case 'payments':
        // Open add payment modal for this personnel
        setSelectedPersonnel(user);
        setPaymentModalOpen(true);
        break;
      case 'delete':
        handleDeleteUser(user.id);
        break;
      default:
        break;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        console.log('🗑️ Deleting user:', userId);
        await axios.delete(createApiUrlWithPrefix(`users/${userId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('✅ User deleted successfully');
        toast.success('User deleted successfully');

        // Check if we need to go back a page after deletion
        const currentUsersCount = users.length;
        console.log('📄 Current users count:', currentUsersCount, 'Current page:', currentPage);

        if (currentUsersCount === 1 && currentPage > 1) {
          console.log('⬅️ Going back to previous page');
          const newPage = Math.max(currentPage - 1, 1);
          setCurrentPage(newPage);
          // Don't call fetchUsers here - let useEffect handle it when currentPage changes
        } else {
          // Only refresh immediately if we're not changing pages
          console.log('🔄 Refreshing users list after delete...');
          await fetchUsers();
        }

        // Force component refresh
        setRefreshKey(prev => prev + 1);
        console.log('✅ Users list refreshed after delete');

        // Refresh sidebar counts
        refreshUserCounts();

        // Additional debugging - check if user is still in the list
        setTimeout(() => {
          console.log('🔍 Checking if deleted user still exists in list...');
          const userStillExists = users.some(user => user.id === userId);
          if (userStillExists) {
            console.warn('⚠️ Deleted user still appears in list! This might be a caching issue.');
          } else {
            console.log('✅ Deleted user successfully removed from list');
          }
        }, 500);
      } catch (error) {
        console.error('Failed to delete user:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);

        // Extract error message from various possible locations
        let errorMessage = 'Failed to delete user';

        if (error.response?.data) {
          const errorData = error.response.data;

          // Check for message property
          if (errorData.message) {
            errorMessage = errorData.message;
          }
          // Check for error property
          else if (errorData.error) {
            errorMessage = errorData.error;
          }
          // Check for string response
          else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
          // Check for detail property
          else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Check for specific error types
        if (error.response?.status === 403) {
          toast.error('Access denied: You do not have permission to delete users. Admin or SuperAdmin role required.');
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized: Please log in again.');
        } else if (error.response?.status === 404) {
          toast.error('User not found.');
        } else if (error.response?.status === 400) {
          toast.error(errorMessage);
        } else if (error.response?.status === 500) {
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setEditingUser(null);
    fetchUsers();
    // Refresh sidebar counts (in case role changed)
    refreshUserCounts();
  };

  // Helper functions
  const getRoleName = (role) => {
    switch (role) {
      case 4: return 'SuperAdmin';
      case 3: return 'Admin';
      default: return 'Unknown';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 4: return 'bg-red-100 text-red-800';
      case 3: return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div key={refreshKey} className="flex-1 flex flex-col min-h-0 bg-neutral-50" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-6">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('personnel.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">
                {t('personnel.subtitle')}
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-primary-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('personnel.addPersonnel')}</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    type="text"
                    placeholder={t('personnel.searchPlaceholder')}
                    value={searchTerm}
                    onChange={handleSearch}
                    className={`w-full py-4 px-12 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Clear
                </button>

                <button
                  onClick={fetchUsers}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* End of Fixed Header Section */}

        {/* Scrollable Cards Section */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 lg:px-8 pb-32">
          {/* Select All and Personnel Cards */}
          <div className="space-y-4">
            {/* Select All Checkbox */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({selectedUsers.length} of {users.length} selected)
                  </span>
                </div>
                {selectedUsers.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {selectedUsers.length} personnel selected
                    </span>
                    <button className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors">
                      Delete Selected
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Personnel Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {users.map((user) => (
                <div key={user.id} className="relative bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  {/* Header with checkbox and role badge */}
                  <div className="flex items-center justify-between p-4 pb-2">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(user.role)}`}>
                      {getRoleName(user.role)}
                    </span>
                  </div>

                  {/* User Info */}
                  <div className="px-4 pb-4">
                    {/* Avatar */}
                    <div className="flex justify-center mb-4">
                      {getUserImageUrl(user) ? (
                        <div className="relative">
                          <img
                            src={getUserImageUrl(user)}
                            alt={user.name || 'Personnel'}
                            className="w-16 h-16 rounded-full object-cover border-2 border-red-100 shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center border-2 border-red-100 shadow-sm hidden">
                            <span className="text-xl font-bold text-red-600">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center border-2 border-red-100 shadow-sm">
                          <span className="text-xl font-bold text-red-600">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Name and Email */}
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {user.name || 'Unknown User'}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    </div>

                    {/* Key Info */}
                    <div className="space-y-2 text-xs text-gray-600">
                      {user.phoneNumber && (
                        <div className="flex items-center justify-center">
                          <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                            📞 {user.phoneNumber}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-center">
                        <span className="bg-red-50 text-red-700 px-3 py-1 rounded-full font-medium">
                          👤 Personnel
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Dropdown - Top Right */}
                  <div className="absolute top-4 right-4 dropdown-container">
                    <button
                      onClick={() => handleDropdownToggle(user.id)}
                      className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {dropdownOpen === user.id && (
                      <div className="absolute right-0 top-10 z-[9999] w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <button
                          onClick={() => handleAction('view', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-3" />
                          {t('common.view')}
                        </button>
                        <button
                          onClick={() => handleAction('edit', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Edit className="w-4 h-4 mr-3" />
                          {t('common.edit')}
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleAction('permissions', user)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <Shield className="w-4 h-4 mr-3" />
                            {t('permissions.management')}
                          </button>
                        )}
                        <button
                          onClick={() => handleAction('payments', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 mr-3" />
                          {t('common.payments')}
                        </button>
                        <button
                          onClick={() => handleAction('delete', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-3" />
                          {t('common.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* End of Scrollable Cards Section */}

        {/* Pagination - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${currentPage === pageNum
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">Next</span>
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Add Payment Modal */}
        <PersonnelAddPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedPersonnel(null);
          }}
          personnel={selectedPersonnel}
          refreshList={fetchUsers}
        />

        {/* User Creation Modal */}
        <UserCreationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          role="Admin"
          onUserCreated={handleUserCreated}
        />

        {/* User Edit Modal */}
        {editingUser && (
          <UserEditModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingUser(null);
            }}
            user={editingUser}
            onUserUpdated={handleUserUpdated}
            allowedRoles={[3, 4]} // Only Admin and SuperAdmin for Personnel page
          />
        )}

        {/* User Permissions Dialog */}
        {permissionsUser && (
          <UserPermissionsDialog
            isOpen={showPermissionsDialog}
            onClose={() => {
              setShowPermissionsDialog(false);
              setPermissionsUser(null);
            }}
            user={permissionsUser}
            onPermissionsUpdated={() => {
              // Optionally refresh data or show success message
              console.log('Permissions updated for user:', permissionsUser.name);
            }}
          />
        )}

        {/* User Details Modal */}
        {viewingUser && (
          <UserDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setViewingUser(null);
            }}
            user={viewingUser}
          />
        )}
      </div>
      {/* End of main flex container */}
    </>
  );
}

export default Personnel;